import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Map } from "lucide-react";
import ClustrLogo from "@/components/icons/ClustrLogo";
import Footer from "@/components/landing/Footer";
import { useMetadata } from "@/hooks/useMetadata";
import { APP_METADATA } from "@/constants/metadata";

const Roadmap = () => {
  const { title, description, image } = APP_METADATA.roadmap;
  
  // Use the metadata hook to set page metadata
  useMetadata({ title, description, image });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="relative">
        <div className="w-full">
          <img 
            src="/lovable-uploads/db274e28-447c-433e-b3cb-6ec6fca4b3f6.png" 
            alt="Clustr Vision Cards" 
            className="w-full object-contain"
          />
        </div>
        
        <div className="absolute top-4 left-0 w-full px-6">
          <div className="max-w-6xl mx-auto">
            <Link to="/">
              <ClustrLogo 
                width={180} 
                height={54} 
                src="/lovable-uploads/16e7eb6e-dfe8-4e43-8a31-12f712d5141f.png"
              />
            </Link>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full px-6 pb-6 bg-gradient-to-t from-white via-white/80 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-5">
              <Map className="h-20 w-20 text-clustr-blue" />
              <h1 className="text-6xl font-bold text-clustr-text">Roadmap</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-grow">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-semibold mt-8 mb-4">Foundation</h2>
            <h3 className="text-xl font-semibold mt-6 mb-3">Disclaimer</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Continued experimental nature of the project</li>
              <li>Current focus on validating core functionality</li>
              <li>Most important aspect is its usefulness and feedback from users</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Sustainability Plan</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Patreon support model (donation-based)</li>
              <li>Transparency about development resources</li>
              <li>Long-term business model considerations</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Near-Term Improvements</h2>
            <h3 className="text-xl font-semibold mt-6 mb-3">Web Clipper Evolution</h3>
            <div className="pl-6">
              <p className="font-medium mb-2">V2: Contextual capture</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Highlighted text with position indexing</li>
                <li>Video timestamp preservation (with position indexing)</li>
                <li>Image selection with position indexing</li>
              </ul>
              
              <p className="font-medium mt-4 mb-2">V3: Enhanced metadata extraction</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Author/publisher information</li>
                <li>Publication date</li>
                <li>Reading time/duration</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold mt-6 mb-3">Core Platform Enhancements</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Sorting and filtering improvements</li>
              <li>Search implementation</li>
              <li>Performance optimizations</li>
              <li>Mobile experience improvements</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Mid-Term Features</h2>
            <h3 className="text-xl font-semibold mt-6 mb-3">Board Capabilities</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Relationship visualization (connection lines)</li>
              <li>Mind mapping layouts</li>
              <li>Multi-axis organization systems</li>
              <li>Export to presentation formats (SVG, PPT)</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Content Management</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Bulk editing capabilities</li>
              <li>Advanced organizational tools</li>
              <li>Improved content preview functionality</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Import/Export Systems</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>CSV import with mapping tools</li>
              <li>CSV export with customization options</li>
              <li>API access for power users (if applicable)</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Long-Term Vision</h2>
            <h3 className="text-xl font-semibold mt-6 mb-3">AI Integration</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Natural language search across content</li>
              <li>Intelligent tagging suggestions</li>
              <li>Automated content organization</li>
              <li>Relationship discovery between items</li>
              <li>Board population and layout</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Collaboration Feature</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Sharing individual items, plus full collections and boards</li>
              <li>Public/private permissions system</li>
              <li>Collaborative boards and collections</li>
              <li>Comment and annotation capabilities</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">If Fully Validated</h2>
            <h3 className="text-xl font-semibold mt-6 mb-3">Platform Evolution</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Complete redesign with new refined UX/UI</li>
              <li>Technical replatforming (Rust implementation)</li>
              <li>Light/dark mode and accessibility improvements</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6 mb-3">Business Model</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Transition to sustainable Freemium+ subscription offering</li>
              <li>Tiered plans for different user needs</li>
              <li>Enterprise capabilities for teams</li>
            </ul>

            <div className="mt-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h2 className="text-2xl font-semibold mb-4">User Input</h2>
              <p className="mb-4">Help us shape the future of Clustr by participating in our feature prioritization survey:</p>
              <div className="flex flex-col space-y-4">
                <a 
                  href="https://forms.gle/yoursurveylink" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-clustr-blue hover:bg-clustr-blue/90 transition-colors"
                >
                  Take the Survey
                </a>
                <p className="text-sm text-gray-600">
                  Your feedback directly influences our development priorities and helps us build a better platform for everyone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Roadmap;
